import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, getCountFromServer, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import AdminStatsCard from '../../components/admin/AdminStatsCard';
import DemoDetailsModal from '../../components/demos/DemoDetailsModal';
import DemoAssignmentModal from '../../components/admin/DemoAssignmentModal';
import DemoRescheduleModal from '../../components/admin/DemoRescheduleModal';
import DemoResetModal from '../../components/admin/DemoResetModal';
import OrderDetails from '../../components/orders/OrderDetails';
import AgentRecords from '../../components/AgentRecords';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  ClockIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

export default function AdminDashboard() {
  const { currentAgent } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    totalDemos: 0,
    upcomingDemos: 0,
    totalOrders: 0,
    activeAgents: 0,
    activeReferrers: 0
  });
  
  const [allDemos, setAllDemos] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [filteredDemos, setFilteredDemos] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [demoSearch, setDemoSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [demoLocationFilter, setDemoLocationFilter] = useState('all');
  const [demoDateFilter, setDemoDateFilter] = useState('all');
  
  // Modals
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showAgentRecords, setShowAgentRecords] = useState(false);

  // Load stats and data
  useEffect(() => {
    const loadAdminData = async () => {
      if (!currentAgent?.id) return;
      
      try {
        setLoading(true);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Get all demos
        const demosRef = collection(db, 'demo_bookings');
        const demosSnapshot = await getDocs(demosRef);
        const demosData = [];
        
        demosSnapshot.forEach((doc) => {
          demosData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort demos by date (descending) - use updatedAt if available, otherwise demoDate
        demosData.sort((a, b) => {
          // Helper function to get the most relevant date for a demo
          const getDemoDate = (demo) => {
            // Try updatedAt first (for processed demos)
            if (demo.payload?.updatedAt) {
              const updatedAt = demo.payload.updatedAt;
              // Handle Firestore Timestamp
              if (updatedAt && typeof updatedAt.toDate === 'function') {
                return updatedAt.toDate();
              }
              // Handle string timestamp
              if (typeof updatedAt === 'string') {
                return new Date(updatedAt);
              }
              // Handle timestamp object
              if (updatedAt && updatedAt.seconds) {
                return new Date(updatedAt.seconds * 1000);
              }
            }
            
            // Fall back to demo date
            const demoDate = demo.payload?.dateTime?.demoDate;
            if (demoDate) {
              return new Date(demoDate);
            }
            
            // Default to old date
            return new Date('1970-01-01');
          };
          
          const dateA = getDemoDate(a);
          const dateB = getDemoDate(b);
          
          // Sort descending (newest first)
          return dateB.getTime() - dateA.getTime();
        });
        
        // Get all orders
        const ordersRef = collection(db, 'orders');
        const ordersQuery = query(ordersRef, orderBy('created_at', 'desc'), limit(100));
        const ordersSnapshot = await getDocs(ordersQuery);
        const ordersData = [];
        
        ordersSnapshot.forEach((doc) => {
          ordersData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Get agent counts
        const agentsRef = collection(db, 'plsss_agents');
        const agentsSnapshot = await getDocs(agentsRef);
        let adminUserCount = 0;
        let referrerCount = 0;
        
        agentsSnapshot.forEach((doc) => {
          const data = doc.data();
          const role = data.apps?.agents?.role || data.role || 'user';
          if (role === 'admin' || role === 'user') {
            adminUserCount++;
          } else if (role === 'referrer') {
            referrerCount++;
          }
        });
        
        // Calculate upcoming demos
        const upcomingDemos = demosData.filter(demo => {
          const demoDate = new Date(demo.payload?.dateTime?.demoDate);
          return demoDate >= today;
        });
        
        setStats({
          totalDemos: demosData.length,
          upcomingDemos: upcomingDemos.length,
          totalOrders: ordersData.length,
          activeAgents: adminUserCount,
          activeReferrers: referrerCount
        });
        
        setAllDemos(demosData);
        setAllOrders(ordersData);
        setFilteredDemos(demosData);
        setFilteredOrders(ordersData);
        
      } catch (error) {
        console.error('Error loading admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAdminData();
  }, [currentAgent?.id]);

  // Filter demos
  useEffect(() => {
    let result = [...allDemos];
    
    // Search filter
    if (demoSearch) {
      const term = demoSearch.toLowerCase();
      result = result.filter(demo => 
        demo.payload?.customer?.name?.toLowerCase().includes(term) ||
        demo.id.toLowerCase().includes(term) ||
        demo.payload?.agentCode?.toLowerCase().includes(term)
      );
    }
    
    // Location filter
    if (demoLocationFilter !== 'all') {
      result = result.filter(demo => demo.payload?.location === demoLocationFilter);
    }
    
    // Date filter
    if (demoDateFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (demoDateFilter === 'upcoming') {
        result = result.filter(demo => {
          const demoDate = new Date(demo.payload?.dateTime?.demoDate);
          return demoDate >= today;
        });
      } else if (demoDateFilter === 'past') {
        result = result.filter(demo => {
          const demoDate = new Date(demo.payload?.dateTime?.demoDate);
          return demoDate < today;
        });
      }
    }
    
    setFilteredDemos(result);
  }, [allDemos, demoSearch, demoLocationFilter, demoDateFilter]);

  // Filter orders
  useEffect(() => {
    let result = [...allOrders];
    
    if (orderSearch) {
      const term = orderSearch.toLowerCase();
      result = result.filter(order => 
        order.customer?.name?.toLowerCase().includes(term) ||
        order.code?.toLowerCase().includes(term) ||
        order.agent_id?.toLowerCase().includes(term)
      );
    }
    
    setFilteredOrders(result);
  }, [allOrders, orderSearch]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeSlot) => {
    if (!timeSlot) return 'No time set';
    
    // Canonical format mapping
    const timeMap = {
      'Morning': 'Morning (9-12)',
      'Afternoon': 'Afternoon (1-5)',
      'Evening': 'Evening (6-9)'
    };
    
    // If it's already in canonical format, return the formatted version
    if (timeMap[timeSlot]) {
      return timeMap[timeSlot];
    }
    
    // Normalize legacy/incorrect formats to canonical format
    const normalizeMap = {
      '9:00 AM - 12:00 PM': 'Morning (9-12)',
      '1:00 PM - 4:00 PM': 'Afternoon (1-5)',
      '5:00 PM - 8:00 PM': 'Evening (6-9)'
    };
    
    return normalizeMap[timeSlot] || timeSlot;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const num = parseFloat(amount);
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">⚙️ Admin Panel</h1>
              <p className="mt-2 text-sm text-gray-600">Manage demos, orders, and agents across the system</p>
            </div>
            <button
              onClick={() => setShowAgentRecords(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <UserIcon className="h-5 w-5 mr-2" />
              View Agent Records
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <AdminStatsCard 
            title="Total Demos" 
            value={stats.totalDemos} 
            icon="📅"
          />
          <AdminStatsCard 
            title="Upcoming Demos" 
            value={stats.upcomingDemos} 
            icon="⏳"
          />
          <AdminStatsCard 
            title="Total Orders" 
            value={stats.totalOrders} 
            icon="🛒"
          />
          <AdminStatsCard 
            title="Active Agents" 
            value={stats.activeAgents} 
            icon="👥"
            subtitle="Admin & Users"
          />
          <AdminStatsCard 
            title="Referrers" 
            value={stats.activeReferrers} 
            icon="🔗"
          />
        </div>

        {/* All Demos Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Demos ({filteredDemos.length})</h2>
          </div>
          
          {/* Filters */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by customer, demo ID, or agent..."
                  value={demoSearch}
                  onChange={(e) => setDemoSearch(e.target.value)}
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              
              {/* Location Filter */}
              <select
                value={demoLocationFilter}
                onChange={(e) => setDemoLocationFilter(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="all">All Locations</option>
                <option value="Bahrain Office">Bahrain Office</option>
                <option value="Dubai Office">Dubai Office</option>
                <option value="Virtual">Virtual</option>
                <option value="Client">Client Location</option>
              </select>
              
              {/* Date Filter */}
              <select
                value={demoDateFilter}
                onChange={(e) => setDemoDateFilter(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="all">All Dates</option>
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
              </select>
            </div>
          </div>
          
          {/* Demos Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hosting Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDemos.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                      No demos found
                    </td>
                  </tr>
                ) : (
                  filteredDemos.slice(0, 20).map((demo) => (
                    <tr key={demo.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{demo.payload?.customer?.name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{demo.payload?.customer?.phone || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {demo.payload?.agentCode || 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {demo.payload?.referrer || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(demo.payload?.dateTime?.demoDate)}</div>
                        <div className="text-sm text-gray-500">{formatTime(demo.payload?.dateTime?.timeSlot)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {demo.payload?.location || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedDemo(demo);
                              setShowDemoModal(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            View
                          </button>
                          {/* Only show Assign for demos with referrers */}
                          {demo.payload?.referrer && (
                            <>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => {
                                  setSelectedDemo(demo);
                                  setShowAssignModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Assign
                              </button>
                            </>
                          )}
                          {/* Only show Reschedule for future demos */}
                          {(() => {
                            const demoDate = new Date(demo.payload?.dateTime?.demoDate);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return demoDate >= today;
                          })() && (
                            <>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => {
                                  setSelectedDemo(demo);
                                  setShowRescheduleModal(true);
                                }}
                                className="text-green-600 hover:text-green-900"
                              >
                                Reschedule
                              </button>
                            </>
                          )}
                          {/* Show Reset for processed/cancelled demos updated in last 7 days */}
                          {(() => {
                            const updatedAt = demo.payload?.updatedAt;
                            let parsedUpdatedAt = null;
                            
                            if (updatedAt) {
                              if (typeof updatedAt === 'string') {
                                parsedUpdatedAt = new Date(updatedAt);
                              } else if (updatedAt && typeof updatedAt.toDate === 'function') {
                                parsedUpdatedAt = updatedAt.toDate();
                              } else if (updatedAt && updatedAt.seconds) {
                                parsedUpdatedAt = new Date(updatedAt.seconds * 1000);
                              }
                            }
                            
                            const sevenDaysAgo = new Date();
                            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                            sevenDaysAgo.setHours(0, 0, 0, 0);
                            const today = new Date();
                            today.setHours(23, 59, 59, 999);
                            
                            const isProcessedOrCancelled = 
                              demo.payload?.status === 'completed' || 
                              demo.payload?.status === 'cancelled' ||
                              demo.payload?.outcome === 'placed_order' ||
                              demo.payload?.outcome === 'did_not_purchase' ||
                              demo.payload?.outcome === 'cancelled_demo';
                            
                            return isProcessedOrCancelled && parsedUpdatedAt && parsedUpdatedAt >= sevenDaysAgo && parsedUpdatedAt <= today;
                          })() && (
                            <>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => {
                                  setSelectedDemo(demo);
                                  setShowResetModal(true);
                                }}
                                className="text-orange-600 hover:text-orange-900"
                              >
                                Reset
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const hostedDemo = demo.payload?.hostedDemo;
                          if (hostedDemo?.isHosted === 'YES' && hostedDemo?.hostDetails) {
                            const host = hostedDemo.hostDetails;
                            let hostInfo = host.name || 'Unknown';
                            
                            // Add code for booking partners
                            if (host.bkpCode) {
                              hostInfo += ` (${host.bkpCode})`;
                            }
                            
                            // Add phone if available
                            if (host.phone) {
                              hostInfo += ` - ${host.phone}`;
                            }
                            
                            return (
                              <div>
                                <div className="font-medium text-green-700">🏠 {hostInfo}</div>
                                {hostedDemo.giftCode && (
                                  <div className="text-xs text-purple-600">🎁 Gift: {hostedDemo.giftName || hostedDemo.giftCode}</div>
                                )}
                              </div>
                            );
                          }
                          return <span className="text-gray-400">-</span>;
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {filteredDemos.length > 20 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">Showing 20 of {filteredDemos.length} demos</p>
            </div>
          )}
        </div>

        {/* All Orders Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Orders ({filteredOrders.length})</h2>
          </div>
          
          {/* Search */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by customer, order code, or agent..."
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>
          
          {/* Orders Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrer</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                      No orders found
                    </td>
                  </tr>
                ) : (
                  filteredOrders.slice(0, 20).map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.code || order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.customer?.name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{order.customer?.phone || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.agent_id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.referrer || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(order.payment?.total_amount_local_currency)} {order.payment?.local_currency || 'BHD'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {filteredOrders.length > 20 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">Showing 20 of {filteredOrders.length} orders</p>
            </div>
          )}
        </div>
      </div>

      {/* Demo Details Modal */}
      {showDemoModal && selectedDemo && (
        <DemoDetailsModal
          demo={selectedDemo}
          onClose={() => {
            setShowDemoModal(false);
            setSelectedDemo(null);
          }}
        />
      )}

      {/* Demo Assignment Modal */}
      {showAssignModal && selectedDemo && (
        <DemoAssignmentModal
          demo={selectedDemo}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedDemo(null);
          }}
          onSuccess={() => {
            // Reload data after successful assignment
            window.location.reload();
          }}
        />
      )}

      {/* Demo Reschedule Modal */}
      {showRescheduleModal && selectedDemo && (
        <DemoRescheduleModal
          demo={selectedDemo}
          onClose={() => {
            setShowRescheduleModal(false);
            setSelectedDemo(null);
          }}
          onSuccess={() => {
            // Reload data after successful reschedule
            window.location.reload();
          }}
        />
      )}

      {/* Demo Reset Modal */}
      {showResetModal && selectedDemo && (
        <DemoResetModal
          demo={selectedDemo}
          onClose={() => {
            setShowResetModal(false);
            setSelectedDemo(null);
          }}
          onSuccess={() => {
            // Reload data after successful reset
            window.location.reload();
          }}
        />
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-medium text-gray-900">Order Details</h2>
              <button 
                onClick={() => {
                  setShowOrderModal(false);
                  setSelectedOrder(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              <OrderDetails order={selectedOrder} />
            </div>
          </div>
        </div>
      )}

      {/* Agent Records Modal */}
      {showAgentRecords && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Agent Records</h2>
            <button 
              onClick={() => setShowAgentRecords(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <AgentRecords />
          </div>
        </div>
      )}
    </div>
  );
}
